import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  type Ref,
  type RefObject,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { cn } from "../lib/utils";
import {
  alertesDe,
  type Brief,
  compterMots,
  type Double,
  type LigneJournal,
  motsInterditsDans,
  TEXTE_MAX,
} from "./contract";
import { compter } from "./format";
import {
  type Brouillon,
  brouillonAProposer,
  ecrireBrouillon,
  effacerBrouillonSi,
  garderSaisie,
  lireBrouillon,
  lireSaisiesGardees,
  oublierSaisie,
  type SaisieGardee,
} from "./histoires-brouillon";
import {
  ALERTE_GRISE,
  ALERTE_JAUNE,
  ALERTE_PANNE,
  ALERTE_ROUGE,
  BadgeProvenance,
  BOUTON_CONTOUR,
  LIEN_BOUTON_PLEIN,
  LIEN_TEXTE,
  Signal,
} from "./histoires-commun";
import {
  type CourantTexte,
  ecrireTexte,
  effacerTexte,
  poserLeCourant,
  poserLeTexte,
  type ResultatEcriture,
  relireLaDouble,
} from "./histoires-requetes";
import {
  creerMoteur,
  type EtatSauvegarde,
  type Instantane,
  type Moteur,
} from "./histoires-sauvegarde";
import {
  declarationDeLaDouble,
  horsFourchetteEnClair,
  ilYA,
  LIBELLE_PROVENANCE,
  morceauxDuCalque,
  normaliser,
  raisonDe,
  saisieDe,
} from "./histoires-textes";

/**
 * Le texte d'une double (maquettes `HistoiresDouble`, `HistoiresDoubleMobile` et
 * `HistoiresEtats`) : la zone de saisie et son calque, la sauvegarde automatique et ses etats,
 * le compteur face a la fourchette, les mots interdits expliques, la provenance, « Repartir de
 * zéro », le 409, le brouillon local, le reseau coupe et le Lab a l'arret.
 *
 * La logique de sauvegarde est dans `histoires-sauvegarde.ts` ; ce fichier la branche sur React
 * et sur la page (fermeture, onglet cache, retour du reseau) et dessine ce qu'elle dit.
 */

/** Le moteur de la double, et ce qu'il dit a chaque rendu. */
export type TexteDeLaDouble = {
  readonly moteur: Moteur;
  readonly instantane: Instantane;
  /** Vrai des que le navigateur a refuse de garder une saisie. */
  readonly stockageRefuse: boolean;
};

export function useTexteDeLaDouble(double: Double): TexteDeLaDouble {
  const client = useQueryClient();
  const [stockageRefuse, setStockageRefuse] = useState(false);
  const [moteur] = useState(() =>
    creerMoteur(
      {
        texte: normaliser(double.texte),
        version: double.texte_version,
        provenance: double.provenance,
      },
      {
        ecrire: (texte, version, keepalive) =>
          ecrireTexte(double.id, { texte, version }, { keepalive }),
        brouillon: {
          ecrire: (brouillon) => {
            if (!ecrireBrouillon(double.id, brouillon)) setStockageRefuse(true);
          },
          effacer: (texte) => effacerBrouillonSi(double.id, texte),
          lire: () => lireBrouillon(double.id),
        },
        apresSauvegarde: (texte, reponse) => poserLeTexte(client, double.id, texte, reponse),
        apresRattrapage: (courant) => poserLeCourant(client, double.id, courant),
        relire: () => relireLaDouble(client, double.id),
      },
    ),
  );
  const instantane = useSyncExternalStore(moteur.abonner, moteur.lire, moteur.lire);
  // Fixee au montage, comme le moteur : l'ecran d'une double se remonte pour une autre double.
  const relireCetteDouble = useRef(() => relireLaDouble(client, double.id));

  useEffect(() => {
    moteur.monter();
    // La page se ferme, ou passe en arriere-plan (un telephone qui change d'appli) : la derniere
    // saisie part tout de suite, avec `keepalive`. Le reseau revient : nouvel essai sans attendre.
    const partir = (): void => {
      void moteur.envoyer(true);
    };
    // Le retour sur la page (l'ordinateur retrouve apres le telephone) : la double se relit, pour
    // montrer ce qui s'est ecrit ailleurs avant la premiere frappe. Seulement quand rien n'attend :
    // ni saisie a enregistrer, ni envoi, ni 409, ni autre ecriture de l'ecran. `adopter` le
    // reverifie a l'arrivee de la relecture.
    const revenir = (): void => {
      const { etat, verrouille } = moteur.lire();
      if (moteur.occupe() || etat.type === "conflit" || verrouille) return;
      relireCetteDouble.current();
    };
    const changerDeVisibilite = (): void => {
      if (document.visibilityState === "hidden") void moteur.envoyer(true);
      else revenir();
    };
    const enLigne = (): void => moteur.reessayer();
    window.addEventListener("pagehide", partir);
    document.addEventListener("visibilitychange", changerDeVisibilite);
    window.addEventListener("focus", revenir);
    window.addEventListener("online", enLigne);
    return () => {
      window.removeEventListener("pagehide", partir);
      document.removeEventListener("visibilitychange", changerDeVisibilite);
      window.removeEventListener("focus", revenir);
      window.removeEventListener("online", enLigne);
      // Quitter la double (precedente, suivante, le livre) : la saisie part en partant.
      moteur.demonter();
    };
  }, [moteur]);

  // Une relecture qui rend un texte plus recent : pris si rien n'attend d'etre enregistre.
  useEffect(() => {
    moteur.adopter(normaliser(double.texte), double.texte_version, double.provenance);
  }, [moteur, double.texte, double.texte_version, double.provenance]);

  return { moteur, instantane, stockageRefuse };
}

/** L'heure qui avance, a la seconde, tant que l'ecran montre un ecart ou un decompte. */
function useMaintenant(actif: boolean): number {
  const [maintenant, setMaintenant] = useState(() => Date.now());
  useEffect(() => {
    if (!actif) return;
    setMaintenant(Date.now());
    const minuterie = setInterval(() => setMaintenant(Date.now()), 1000);
    return () => clearInterval(minuterie);
  }, [actif]);
  return maintenant;
}

function dansSecondes(prochainEssai: number, maintenant: number): number {
  return Math.max(0, Math.ceil((prochainEssai - maintenant) / 1000));
}

// ---------------------------------------------------------------------------------------
// L'etat de la sauvegarde.

type Ton = "neutre" | "vert" | "ambre" | "rouge";

/** Le point et le libelle de chaque etat (maquette `HistoiresEtats`, « La sauvegarde, pas à pas »). */
export function libelleDeLaSauvegarde(etat: EtatSauvegarde): { texte: string; ton: Ton } | null {
  switch (etat.type) {
    case "repos":
      return null;
    case "modifie":
      return { texte: "Modifié", ton: "neutre" };
    case "envoi":
      return { texte: "Enregistrement...", ton: "neutre" };
    case "enregistre":
      return { texte: "Enregistré", ton: "vert" };
    case "hors_ligne":
      return { texte: "Hors ligne, gardé ici", ton: "ambre" };
    case "arret":
      return { texte: "Non enregistré : Lab à l'arrêt, gardé ici", ton: "rouge" };
    case "session":
      return { texte: "Non enregistré : connexion requise, gardé ici", ton: "rouge" };
    case "refus":
    case "conflit":
      return { texte: "Non enregistré", ton: "rouge" };
  }
}

function StatutSauvegarde({ etat, className }: { etat: EtatSauvegarde; className?: string }) {
  const maintenant = useMaintenant(etat.type === "enregistre");
  const libelle = libelleDeLaSauvegarde(etat);
  return (
    <p
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap text-sm",
        libelle?.ton === "ambre" && "font-medium text-fourchette",
        libelle?.ton === "rouge" && "font-medium text-destructive",
        (libelle === null || libelle.ton === "neutre" || libelle.ton === "vert") &&
          "text-muted-foreground",
        className,
      )}
    >
      <span className="inline-flex items-center gap-1.5" role="status">
        {libelle === null ? null : (
          <>
            {libelle.ton === "vert" ? (
              <span aria-hidden="true" className="font-bold text-provenance-ecrit">
                ✓
              </span>
            ) : (
              <span
                aria-hidden="true"
                className={cn(
                  "inline-block size-2 flex-none rounded-full",
                  etat.type === "modifie" && "border-[1.5px] border-muted-foreground",
                  etat.type === "envoi" && "bg-muted-foreground",
                  libelle.ton === "ambre" && "bg-fourchette",
                  libelle.ton === "rouge" && "bg-destructive",
                )}
              />
            )}
            {libelle.texte}
          </>
        )}
      </span>
      {etat.type === "enregistre" ? (
        <span aria-hidden="true">{ilYA(maintenant - etat.le)}</span>
      ) : null}
    </p>
  );
}

// ---------------------------------------------------------------------------------------
// La zone de saisie et son calque.

/**
 * La typographie commune a la zone et a son calque : meme police, meme taille, meme interligne,
 * memes marges et meme retour a la ligne (decision 11). 16 px au telephone : en dessous, iOS
 * agrandit la page a chaque focus.
 */
const TYPO_DU_TEXTE =
  "whitespace-pre-wrap [overflow-wrap:break-word] px-4 py-3.5 text-base leading-[1.7] md:px-[18px] md:py-4 md:text-[17px]";

/**
 * La zone a la hauteur de son texte, bords compris (`box-sizing: border-box`). Sans mise en page
 * (jsdom), `scrollHeight` vaut 0 : la hauteur minimale de la feuille de style reste.
 */
function ajusterLaHauteur(element: HTMLTextAreaElement | null): void {
  if (element === null) return;
  element.style.height = "auto";
  if (element.scrollHeight > 0) {
    const bords = element.offsetHeight - element.clientHeight;
    element.style.height = `${element.scrollHeight + bords}px`;
  }
}

/**
 * Le texte, et derriere lui un calque qui surligne les mots interdits (decision 11) : le calque
 * porte exactement le meme texte, transparent, aux memes positions ; seuls ses surlignages se
 * voient, sous les lettres de la zone. Le texte reste du texte brut, et un mot interdit
 * n'empeche jamais d'enregistrer.
 *
 * La zone grandit avec son texte : elle ne defile jamais, le calque n'a donc rien a suivre.
 */
function ZoneDeTexte({
  id,
  texte,
  interdits,
  fige,
  decritePar,
  saisir,
  quitter,
  zone,
}: {
  id: string;
  texte: string;
  interdits: Brief["mots_interdits"];
  fige: boolean;
  decritePar: string;
  saisir: (texte: string) => void;
  quitter: () => void;
  zone: RefObject<HTMLTextAreaElement | null>;
}) {
  const morceaux = useMemo(
    () => morceauxDuCalque(texte, motsInterditsDans(texte, interdits)),
    [texte, interdits],
  );

  // Apres chaque rendu (une frappe en est un) et a chaque changement de largeur de la fenetre.
  useLayoutEffect(() => {
    ajusterLaHauteur(zone.current);
  });
  useEffect(() => {
    const ajuster = (): void => ajusterLaHauteur(zone.current);
    window.addEventListener("resize", ajuster);
    return () => window.removeEventListener("resize", ajuster);
  }, [zone]);

  return (
    <div className={cn("relative rounded-[10px]", fige ? "bg-muted" : "bg-card")}>
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none absolute inset-0 overflow-hidden rounded-[10px] border border-transparent text-transparent",
          TYPO_DU_TEXTE,
        )}
        data-calque=""
      >
        {morceaux.map((morceau, rang) =>
          morceau.interdit ? (
            <mark
              className="rounded-[3px] bg-interdit/20 text-transparent underline decoration-interdit decoration-2 underline-offset-[3px]"
              // biome-ignore lint/suspicious/noArrayIndexKey: morceaux recalcules du texte, sans identite
              key={rang}
            >
              {morceau.texte}
            </mark>
          ) : (
            // biome-ignore lint/suspicious/noArrayIndexKey: morceaux recalcules du texte, sans identite
            <span key={rang}>{morceau.texte}</span>
          ),
        )}
      </div>
      <textarea
        aria-describedby={decritePar}
        className={cn(
          "relative block min-h-[250px] w-full resize-none overflow-hidden rounded-[10px] border bg-transparent text-foreground placeholder:text-muted-foreground focus-visible:outline-none md:min-h-[300px]",
          TYPO_DU_TEXTE,
          fige
            ? "border-dashed border-muted-foreground/60"
            : "border-input focus-visible:border-foreground focus-visible:ring-[3px] focus-visible:ring-ring/30",
        )}
        id={id}
        maxLength={TEXTE_MAX}
        onBlur={quitter}
        onChange={(evenement) => saisir(evenement.target.value)}
        placeholder="Écris ici. Tout s'enregistre pendant que tu tapes."
        readOnly={fige}
        ref={zone}
        spellCheck
        value={texte}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------------------
// La carte du texte.

/** Ce qu'une ecriture refusee dit sous le bouton qui l'a lancee. */
export function messageDeResultat(resultat: ResultatEcriture): string {
  switch (resultat.type) {
    case "hors_ligne":
      return "La console ne répond pas : rien n'a changé. Réessaie quand le réseau revient.";
    case "arret":
      return "Le Lab est à l'arrêt : rien n'a changé.";
    case "session":
      return "Ta session a pris fin : reconnecte-toi, puis recommence.";
    case "refus":
      return resultat.message;
    default:
      return "";
  }
}

/** Qui a ecrit, d'apres le 409 : Claude, toi ailleurs, ou on ne sait pas. */
function titreDuConflit(courant: CourantTexte): string {
  if (courant.derniere_origine === "claude") return "Claude vient d'écrire cette double.";
  if (courant.derniere_origine === "nicolas")
    return "Tu as modifié cette double sur un autre écran.";
  return "Cette double a changé entre-temps.";
}

type Copie = "copie" | "selection" | null;

/** Copie la saisie ; sans presse-papiers, la selectionne pour un Ctrl+C. */
async function copier(texte: string, zone: HTMLTextAreaElement | null): Promise<Copie> {
  try {
    await navigator.clipboard.writeText(texte);
    return "copie";
  } catch {
    zone?.focus();
    zone?.select();
    return "selection";
  }
}

function BoutonCopier({
  texte,
  zone,
  refBouton,
}: {
  texte: string;
  zone: HTMLTextAreaElement | null;
  refBouton?: Ref<HTMLButtonElement> | undefined;
}) {
  const [copie, setCopie] = useState<Copie>(null);
  return (
    <span className="inline-flex items-center gap-2">
      <button
        className={cn(BOUTON_CONTOUR, "min-h-8 px-3 py-1")}
        onClick={() => {
          void copier(texte, zone).then(setCopie);
        }}
        ref={refBouton}
        type="button"
      >
        Copier ma saisie
      </button>
      <span className="text-xs text-muted-foreground" role="status">
        {copie === "copie" ? "Copiée." : null}
        {copie === "selection" ? "Sélectionnée : Ctrl+C pour la copier." : null}
      </span>
    </span>
  );
}

/**
 * Une saisie que la console n'a pas, gardee visible pour la copier (409). Posee avant la zone de
 * texte ; tout juste gardee, elle se met en vue et prend le focus, pour qu'au telephone le clavier
 * ne la cache pas sous la zone.
 */
function SaisieFigee({
  titre,
  texte,
  oublier,
  mettreEnVue = false,
  pasGardee = false,
}: {
  titre: string;
  texte: string;
  oublier?: (() => void) | undefined;
  mettreEnVue?: boolean;
  /** Le navigateur a refuse de la garder : elle ne vit que dans cette page. */
  pasGardee?: boolean;
}) {
  const id = useId();
  const [zone, setZone] = useState<HTMLTextAreaElement | null>(null);
  const region = useRef<HTMLElement>(null);
  const copierLaSaisie = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!mettreEnVue) return;
    region.current?.scrollIntoView?.({ block: "nearest" });
    copierLaSaisie.current?.focus();
  }, [mettreEnVue]);

  return (
    <section aria-label={titre} className="flex flex-col gap-2" ref={region}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label
          className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
          htmlFor={id}
        >
          {titre}
        </label>
        <span className="flex flex-wrap items-center gap-2">
          <BoutonCopier refBouton={copierLaSaisie} texte={texte} zone={zone} />
          {oublier === undefined ? null : (
            <button
              className={cn(BOUTON_CONTOUR, "min-h-8 px-3 py-1")}
              onClick={oublier}
              type="button"
            >
              Oublier
            </button>
          )}
        </span>
      </div>
      <textarea
        className="min-h-24 w-full resize-y rounded-[10px] border border-dashed border-muted-foreground/60 bg-muted px-4 py-3 text-base leading-relaxed"
        id={id}
        readOnly
        ref={setZone}
        value={texte}
      />
      {pasGardee ? (
        <p className="text-xs text-destructive">
          Ce navigateur ne la garde pas : copie-la avant de quitter la page.
        </p>
      ) : null}
    </section>
  );
}

export function CarteTexte({
  double,
  titre,
  brief,
  livreId,
  journal,
  texte: { moteur, instantane, stockageRefuse },
  refusDeRelecture = null,
}: {
  double: Double;
  /** Le titre de la double : « 7. Les grands pleurent ». */
  titre: string;
  brief: Brief;
  livreId: number;
  journal: readonly LigneJournal[];
  texte: TexteDeLaDouble;
  /**
   * La relecture de la double est refusee (401, 403) alors qu'une saisie attend : la zone est
   * figee et copiable, jusqu'a ce qu'une reconnexion la laisse s'enregistrer.
   */
  refusDeRelecture?: number | null;
}) {
  const client = useQueryClient();
  const idZone = useId();
  const idCompteur = useId();
  const idInterdits = useId();
  const zone = useRef<HTMLTextAreaElement | null>(null);
  // A l'ouverture : les saisies gardees apres un « Recharger » (elles ont survecu a la page), et
  // le brouillon a proposer, sauf s'il redit une saisie deja gardee (un seul endroit ou la prendre).
  const [depart] = useState(() => {
    const gardees = lireSaisiesGardees(double.id);
    const propose = brouillonAProposer(double.id, normaliser(double.texte));
    if (propose !== null && gardees.some((gardee) => gardee.texte === propose.texte)) {
      effacerBrouillonSi(double.id, propose.texte);
      return { gardees, propose: null };
    }
    return { gardees, propose };
  });
  const [brouillon, setBrouillon] = useState<Brouillon | null>(depart.propose);
  // Les saisies d'avant « Recharger », la plus recente d'abord, gardees visibles pour les copier.
  const [saisiesGardees, setSaisiesGardees] = useState<readonly SaisieGardee[]>(depart.gardees);
  // La saisie tout juste gardee, a mettre en vue ; et celles que le navigateur n'a pas gardees.
  const [aMettreEnVue, setAMettreEnVue] = useState<SaisieGardee | null>(null);
  const [pasGardees, setPasGardees] = useState<readonly SaisieGardee[]>([]);

  const { etat, texte } = instantane;
  const conflit = etat.type === "conflit" ? etat.courant : null;
  const fourchette = double.type === "histoire" ? brief.mots_par_double : null;
  const mots = compterMots(texte);
  const alertes = alertesDe(texte, {
    mots_interdits: brief.mots_interdits,
    mots_par_double: fourchette,
  });
  const alerteMots = alertes.find((alerte) => alerte.type === "mot_interdit");
  const horsFourchette = alertes.find((alerte) => alerte.type === "hors_fourchette");
  const nonEnregistree =
    conflit !== null && texte !== normaliser(conflit.texte) && texte !== instantane.enregistre;

  // Un brouillon attend « Reprendre » ou « Laisser » : la zone est figee, et Restaurer comme
  // Repartir de zero attendent (ils remplaceraient le texte sous le brouillon).
  useEffect(() => {
    if (brouillon === null) moteur.deverrouiller("brouillon");
    else moteur.verrouiller("brouillon");
  }, [brouillon, moteur]);

  // Une relecture rend le texte du brouillon (il etait parti, sa reponse s'etait perdue) : la
  // console l'a, il n'y a plus rien a reprendre.
  const { enregistre } = instantane;
  useEffect(() => {
    if (brouillon === null || brouillon.texte !== enregistre) return;
    effacerBrouillonSi(double.id, brouillon.texte);
    setBrouillon(null);
  }, [brouillon, enregistre, double.id]);

  const recharger = (): void => {
    if (conflit === null) return;
    moteur.recharger(conflit);
    relireLaDouble(client, double.id);
    if (!nonEnregistree) {
      zone.current?.focus();
      return;
    }
    // La saisie d'avant garde sa propre cle jusqu'a « Oublier » : une frappe suivante remplacera
    // le brouillon, jamais elle. Elle se met en vue, avant la zone.
    const gardee: SaisieGardee = { texte, le: new Date().toISOString() };
    if (!garderSaisie(double.id, gardee)) setPasGardees((avant) => [...avant, gardee]);
    setSaisiesGardees((avant) => [gardee, ...avant.filter((autre) => autre.texte !== texte)]);
    setAMettreEnVue(gardee);
  };

  const oublier = (saisie: SaisieGardee): void => {
    oublierSaisie(double.id, saisie);
    // Le brouillon ne s'efface que s'il garde encore cette saisie : une frappe tapee depuis
    // « Recharger » l'a remplace, et elle n'est peut-etre pas encore enregistree.
    effacerBrouillonSi(double.id, saisie.texte);
    setSaisiesGardees((avant) => avant.filter((autre) => autre !== saisie));
    zone.current?.focus();
  };

  const reprendre = (): void => {
    if (brouillon === null) return;
    moteur.reprendre(brouillon);
    setBrouillon(null);
    zone.current?.focus();
  };

  const laisser = (): void => {
    // Seulement ce brouillon-la : un autre onglet a pu en poser un plus recent depuis.
    if (brouillon !== null) effacerBrouillonSi(double.id, brouillon.texte);
    setBrouillon(null);
    zone.current?.focus();
  };

  return (
    <section
      aria-labelledby="histoires-titre-texte"
      className="flex min-w-0 flex-col gap-3 md:rounded-lg md:border md:border-border md:bg-card md:p-5 md:text-card-foreground"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 max-md:hidden">
        <div className="flex items-center gap-2.5">
          <h2 className="text-base font-semibold" id="histoires-titre-texte">
            Texte
          </h2>
          <BadgeProvenance provenance={instantane.provenance} />
        </div>
        <StatutSauvegarde etat={etat} />
      </div>

      {refusDeRelecture === null ? null : (
        <div className={ALERTE_PANNE} role="alert">
          <p>
            <strong className="font-semibold">
              {refusDeRelecture === 403
                ? "Cette adresse n'est plus sur la liste de la console."
                : "Ta session a pris fin."}
            </strong>{" "}
            La double ne se relit plus : ta saisie reste ici, figée. Copie-la, reconnecte-toi dans
            un autre onglet, puis réessaie.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <BoutonCopier texte={texte} zone={zone.current} />
            <button
              className={BOUTON_CONTOUR}
              onClick={() => {
                // Reconnecte ailleurs : la saisie en attente repart, et la double se relit, meme
                // quand plus rien n'attend (apres « Recharger », seule la relecture rend la zone).
                moteur.reessayer();
                relireLaDouble(client, double.id);
              }}
              type="button"
            >
              Réessayer
            </button>
          </div>
        </div>
      )}

      {brouillon === null ? null : (
        <div className={ALERTE_JAUNE} role="alert">
          <p>
            <strong className="font-semibold">
              Une saisie {saisieDe(brouillon.le, Date.now())} n'est pas enregistrée
            </strong>{" "}
            sur cet appareil. Reprise, elle part avec la version sur laquelle tu l'as tapée : si la
            double a changé depuis, la console le dira.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              className={cn(LIEN_BOUTON_PLEIN, "min-h-8 px-3 py-1")}
              onClick={reprendre}
              type="button"
            >
              Reprendre ma saisie
            </button>
            <button
              className={cn(BOUTON_CONTOUR, "min-h-8 px-3 py-1")}
              onClick={laisser}
              type="button"
            >
              Laisser
            </button>
          </div>
        </div>
      )}

      {conflit === null ? null : (
        <div className={ALERTE_JAUNE} role="alert">
          <p>
            <strong className="font-semibold">{titreDuConflit(conflit)}</strong>{" "}
            {nonEnregistree
              ? "Ta dernière saisie n'est pas enregistrée, et la zone de texte attend que tu recharges."
              : "Recharge pour voir le texte enregistré avant de continuer."}
          </p>
          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-2">
            <button
              className={cn(LIEN_BOUTON_PLEIN, "max-md:w-full")}
              onClick={recharger}
              type="button"
            >
              Recharger
            </button>
            {nonEnregistree ? (
              // Au telephone aussi : il dit ce que « Recharger » va faire de la saisie.
              <span className="text-sm">
                {conflit.derniere_origine === "claude"
                  ? "le texte de Claude prend la place ; ta saisie reste copiable dessous"
                  : "le texte enregistré prend la place ; ta saisie reste copiable dessous"}
              </span>
            ) : null}
          </div>
        </div>
      )}

      {conflit !== null && nonEnregistree ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Ta saisie non enregistrée
          </span>
          <BoutonCopier texte={texte} zone={zone.current} />
        </div>
      ) : null}

      {/* Avant la zone : visibles sans defiler, jamais caches par le clavier du telephone. */}
      {saisiesGardees.map((saisie, rang) => (
        <SaisieFigee
          key={`${saisie.le}|${saisie.texte.length}`}
          mettreEnVue={saisie === aMettreEnVue}
          oublier={() => oublier(saisie)}
          pasGardee={pasGardees.includes(saisie)}
          texte={saisie.texte}
          titre={
            rang === 0
              ? "Ta saisie d'avant, non enregistrée"
              : `Une saisie plus ancienne (${saisieDe(saisie.le, Date.now()).replace(/^(de|du) /, "")}), non enregistrée`
          }
        />
      ))}

      <label className="sr-only" htmlFor={idZone}>
        Texte de la double
      </label>
      <ZoneDeTexte
        decritePar={alerteMots === undefined ? idCompteur : `${idCompteur} ${idInterdits}`}
        fige={
          conflit !== null ||
          brouillon !== null ||
          instantane.verrouille ||
          refusDeRelecture !== null
        }
        id={idZone}
        interdits={brief.mots_interdits}
        quitter={moteur.quitter}
        saisir={moteur.saisir}
        texte={texte}
        zone={zone}
      />

      <div className="flex flex-col gap-1.5" id={idCompteur}>
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm">
          <span>
            <strong
              className={cn(
                "font-semibold tabular-nums",
                horsFourchette !== undefined && "text-fourchette",
              )}
            >
              {compter(mots, "mot", "mots")}
            </strong>
            {fourchette === null ? null : (
              <>
                <span className="text-muted-foreground max-md:hidden">
                  {" "}
                  sur une fourchette de {fourchette.min} à {fourchette.max}
                </span>
                <span className="text-muted-foreground md:hidden">
                  {" "}
                  · de {fourchette.min} à {fourchette.max}
                </span>
              </>
            )}
          </span>
          <span className="text-muted-foreground max-md:hidden">
            mots interdits : {alerteMots === undefined ? 0 : alerteMots.mots.length}
          </span>
          <StatutSauvegarde className="md:hidden" etat={etat} />
        </div>
        {fourchette === null ? null : (
          <span aria-hidden="true" className="block h-1.5 overflow-hidden rounded-full bg-muted">
            <span
              className={cn(
                "block h-full rounded-full",
                horsFourchette === undefined ? "bg-muted-foreground" : "bg-fourchette",
              )}
              style={{ width: `${Math.min(100, (mots / Math.max(1, fourchette.max)) * 100)}%` }}
            />
          </span>
        )}
        {horsFourchette === undefined || horsFourchette.type !== "hors_fourchette" ? null : (
          <Signal ton="ambre">{horsFourchetteEnClair(horsFourchette)}</Signal>
        )}
        {/* Au telephone, le pied du texte n'est pas la : la provenance qui compte pour KDP se
            dit ici, sous le compteur. Un texte genere le reste, meme retouche a la main. */}
        {instantane.provenance === "genere" ? (
          <p className="text-xs font-medium text-provenance-genere md:hidden">
            Généré : écrit par Claude. Il reste à déclarer à KDP, même retouché.
          </p>
        ) : null}
        {instantane.provenance === "assiste" ? (
          <p className="text-xs text-muted-foreground md:hidden">
            {declarationDeLaDouble("assiste")}
          </p>
        ) : null}
      </div>

      {alerteMots === undefined || alerteMots.type !== "mot_interdit" ? null : (
        <div className={ALERTE_ROUGE} id={idInterdits}>
          <ul className="flex flex-col gap-1">
            {alerteMots.mots.map((mot) => {
              const raison = raisonDe(mot, brief.mots_interdits);
              return (
                <li key={mot}>
                  <strong className="font-semibold">« {mot} »</strong> est un mot interdit du brief.
                  {raison === null ? null : ` ${raison}`}
                </li>
              );
            })}
          </ul>
          <Link
            className={cn(LIEN_TEXTE, "mt-1 inline-block text-interdit max-md:hidden")}
            params={{ id: String(livreId) }}
            search={{ onglet: "brief" }}
            to="/histoires/livres/$id"
          >
            Voir le brief
          </Link>
        </div>
      )}

      {/* Une relecture refusee dit deja la session perdue, avec son « Réessayer ». */}
      {refusDeRelecture !== null && etat.type === "session" ? null : (
        <MessageDePanne etat={etat} reessayer={moteur.reessayer} stockageRefuse={stockageRefuse} />
      )}

      {instantane.relu ? (
        <p className={cn(ALERTE_GRISE, "text-xs")} role="status">
          Le texte a changé ailleurs : la double montre le dernier enregistré.
        </p>
      ) : null}

      <PiedDuTexte
        double={double}
        instantane={instantane}
        journal={journal}
        moteur={moteur}
        mots={mots}
        titre={titre}
        zone={zone}
      />
    </section>
  );
}

/** Hors ligne, Lab a l'arret, session perdue, refus : ce qui arrive a la saisie, et « Réessayer ». */
function MessageDePanne({
  etat,
  reessayer,
  stockageRefuse,
}: {
  etat: EtatSauvegarde;
  reessayer: () => void;
  stockageRefuse: boolean;
}) {
  const prochainEssai =
    etat.type === "hors_ligne" || etat.type === "arret"
      ? etat.prochainEssai
      : etat.type === "refus"
        ? etat.prochainEssai
        : null;
  const maintenant = useMaintenant(prochainEssai !== null);
  const gardee = stockageRefuse
    ? "ta saisie reste dans cette page seulement, ce navigateur ne garde rien : ne la ferme pas"
    : "ta saisie est gardée sur cet appareil";
  const decompte =
    prochainEssai === null
      ? null
      : ` Nouvel essai dans ${dansSecondes(prochainEssai, maintenant)} s.`;
  // Un vrai bouton, hors de la phrase et d'au moins 40 px : c'est l'action de reprise, au doigt.
  const bouton = (
    <button className={cn(BOUTON_CONTOUR, "mt-2")} onClick={reessayer} type="button">
      Réessayer
    </button>
  );

  switch (etat.type) {
    case "hors_ligne":
      return (
        <div className={ALERTE_JAUNE}>
          <p>
            Pas de réseau : {gardee}.{decompte}
          </p>
          {bouton}
        </div>
      );
    case "arret":
      return (
        <div className={ALERTE_PANNE}>
          <p>
            <strong className="font-semibold">
              Le Lab est à l'arrêt (stop_all, ou console en pause).
            </strong>{" "}
            Rien ne s'enregistre : {gardee}, et partira quand l'arrêt sera levé.{decompte}
          </p>
          {bouton}
        </div>
      );
    case "session":
      return (
        <div className={ALERTE_PANNE}>
          <p>
            <strong className="font-semibold">
              {etat.statut === 403
                ? "Cette adresse n'est plus sur la liste de la console."
                : "Ta session a pris fin."}
            </strong>{" "}
            Rien ne s'enregistre : {gardee}. Reconnecte-toi dans un autre onglet, puis réessaie.
          </p>
          {bouton}
        </div>
      );
    case "refus":
      return (
        <div className={ALERTE_PANNE}>
          <p>
            <strong className="font-semibold">Non enregistré</strong> ({etat.message}) : {gardee}.
            {decompte}
          </p>
          {bouton}
        </div>
      );
    default:
      return null;
  }
}

/** Le telephone, tel que Tailwind le lit (`max-md`) : le pied du texte y est cache. */
const SOUS_MD = "(width < 48rem)";

/**
 * Sous le texte, sur grand ecran : la declaration de la double, l'avertissement d'un texte
 * genere, et « Repartir de zéro » avec sa confirmation.
 */
function PiedDuTexte({
  double,
  titre,
  instantane,
  moteur,
  journal,
  mots,
  zone,
}: {
  double: Double;
  titre: string;
  instantane: Instantane;
  moteur: Moteur;
  journal: readonly LigneJournal[];
  mots: number;
  zone: { readonly current: HTMLTextAreaElement | null };
}) {
  const client = useQueryClient();
  const [confirmation, setConfirmation] = useState<"fermee" | "ouverte" | "annulee">("fermee");
  const [envoi, setEnvoi] = useState(false);
  const [echec, setEchec] = useState<string | null>(null);
  const bouton = useRef<HTMLButtonElement>(null);
  const annuler = useRef<HTMLButtonElement>(null);
  // Vrai du clic sur « Repartir de zéro » a la fin : ni Annuler, ni Echap, ni un second clic.
  const enCours = useRef(false);
  // Faux une fois l'ecran parti : un effacement ne part jamais sans l'ecran qui le montre.
  const monte = useRef(true);
  const { provenance } = instantane;
  const conflit = instantane.etat.type === "conflit";
  // Une saisie en attente n'empeche pas d'ouvrir : elle part d'abord, a la confirmation. Une
  // autre ecriture de l'ecran (restauration, brouillon a reprendre) tient la zone : on attend.
  const autreEcriture = instantane.verrous.some((raison) => raison !== "confirmation");
  const bloque = conflit || envoi || autreEcriture;
  // Une double vide (texte `null`) n'a rien a effacer : la console le refuserait (409 double_vide).
  const vide = provenance === null && instantane.texte === "";
  const retouche = journal[0]?.origine === "nicolas";

  // Ouverte, la confirmation tient la zone : ce qui s'y taperait ne passerait pas au journal.
  // Le focus va sur « Annuler », jamais sur le bouton qui efface ; Echap la ferme, et le focus
  // revient au bouton qui l'a ouverte.
  useEffect(() => {
    if (confirmation !== "ouverte") {
      moteur.deverrouiller("confirmation");
      if (confirmation === "annulee") bouton.current?.focus();
      return;
    }
    moteur.verrouiller("confirmation");
    annuler.current?.focus();
    const echap = (evenement: KeyboardEvent): void => {
      // Pendant l'effacement, Echap ne referme rien : il irait au bout quand meme.
      if (evenement.key !== "Escape" || enCours.current) return;
      evenement.preventDefault();
      setConfirmation("annulee");
    };
    window.addEventListener("keydown", echap);
    return () => window.removeEventListener("keydown", echap);
  }, [confirmation, moteur]);

  useEffect(() => {
    monte.current = true;
    return () => {
      monte.current = false;
      moteur.deverrouiller("confirmation");
    };
  }, [moteur]);

  // Au telephone, le pied du texte est cache : une confirmation ouverte y tiendrait la zone sans
  // issue visible. Elle se ferme au passage sous `md`, une fois l'effacement en cours fini.
  useEffect(() => {
    if (confirmation !== "ouverte" || envoi || typeof window.matchMedia !== "function") return;
    const requete = window.matchMedia(SOUS_MD);
    const fermer = (): void => {
      if (requete.matches) setConfirmation("fermee");
    };
    fermer();
    requete.addEventListener?.("change", fermer);
    return () => requete.removeEventListener?.("change", fermer);
  }, [confirmation, envoi]);

  const repartir = async (): Promise<void> => {
    if (enCours.current) return;
    enCours.current = true;
    setEnvoi(true);
    setEchec(null);
    // Du clic a la fin, l'effacement tient la zone : rien ne s'y tape qu'il effacerait.
    moteur.verrouiller("effacement");
    try {
      // La saisie en attente part d'abord : elle passe au journal, comme la confirmation le promet.
      if (!(await moteur.vider())) {
        if (moteur.lire().etat.type === "conflit") {
          setConfirmation("fermee");
          return;
        }
        setEchec(
          "Ta saisie n'a pas pu être enregistrée : rien n'est effacé. Réessaie une fois qu'elle l'est.",
        );
        return;
      }
      // L'ecran est parti pendant la vidange (une autre double, le livre) : rien ne s'efface.
      if (!monte.current) return;
      const resultat = await effacerTexte(double.id, moteur.lire().version);
      if (resultat.type === "ok") {
        // Refuse si une saisie attendait malgre tout : la prochaine ecriture fera le 409, qui la garde.
        moteur.remplacer("", resultat.reponse.texte_version, resultat.reponse.provenance);
        relireLaDouble(client, double.id);
        setConfirmation("fermee");
        zone.current?.focus();
        return;
      }
      if (resultat.type === "conflit") {
        moteur.signalerConflit(resultat.courant);
        setConfirmation("fermee");
        return;
      }
      if (resultat.type === "refus" && resultat.code === "double_vide") {
        // La double est deja vide ailleurs : rien a effacer, rien de perdu. La relecture la montre.
        relireLaDouble(client, double.id);
        setConfirmation("fermee");
        return;
      }
      setEchec(messageDeResultat(resultat));
    } finally {
      enCours.current = false;
      setEnvoi(false);
      moteur.deverrouiller("effacement");
    }
  };

  return (
    <div className="flex flex-col gap-3 max-md:hidden">
      {provenance === "genere" ? (
        <p className={ALERTE_JAUNE}>
          {retouche
            ? "Claude a proposé ce texte ; tu l'as retouché depuis. Il reste généré, "
            : "Claude a écrit ce texte : il reste généré même retouché, "}
          et le livre déclarera du texte généré par IA : KDP le compte « AI-generated even if you
          applied substantial edits afterwards ». Pour que la double redevienne la tienne : Repartir
          de zéro.
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-3">
        <p className="text-xs text-muted-foreground">{declarationDeLaDouble(provenance)}</p>
        {vide || confirmation === "ouverte" ? null : (
          <button
            className="inline-flex min-h-10 items-center justify-center rounded-md border border-destructive bg-background px-4 py-2 text-sm font-medium text-destructive transition hover:bg-destructive/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
            disabled={bloque}
            onClick={() => {
              setEchec(null);
              setConfirmation("ouverte");
            }}
            ref={bouton}
            type="button"
          >
            Repartir de zéro
          </button>
        )}
      </div>
      {confirmation === "ouverte" ? (
        <section aria-labelledby="histoires-repartir-titre" className={ALERTE_GRISE}>
          <h3 className="font-semibold" id="histoires-repartir-titre">
            Repartir de zéro sur « {titre} » ?
          </h3>
          <p className="mt-1.5">
            Le texte actuel ({compter(mots, "mot", "mots")}
            {provenance === null ? "" : `, ${LIBELLE_PROVENANCE[provenance]}`}) passe au journal :
            tu pourras le restaurer. La double redevient vide, sans provenance, et ce que tu écriras
            ensuite sera « écrit ».
          </p>
          <p className={cn(ALERTE_JAUNE, "mt-2.5")}>
            Écris sans recopier : un texte repris de Claude reste du texte généré, même retapé à la
            main.
          </p>
          {echec === null ? null : (
            <p className="mt-2 text-sm text-destructive" role="alert">
              {echec}
            </p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            {/* Le rouge des mots interdits, texte en `--card` : le contraste mesure du bouton
                « À jeter » presse du coloriage, 6,6:1 en clair et 7,5:1 en sombre. */}
            <button
              aria-disabled={envoi || undefined}
              className="inline-flex min-h-10 items-center justify-center rounded-md bg-interdit px-4 py-2 text-sm font-medium text-card transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              onClick={() => {
                void repartir();
              }}
              type="button"
            >
              Repartir de zéro
            </button>
            {/* Pendant l'effacement, Annuler ne peut plus rien arreter : il ne promet rien. */}
            <button
              aria-disabled={envoi || undefined}
              className={cn(
                BOUTON_CONTOUR,
                "aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
              )}
              onClick={() => {
                if (!enCours.current) setConfirmation("annulee");
              }}
              ref={annuler}
              type="button"
            >
              Annuler
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
}
